import type { TaskListItemDto } from '@ovo/shared'
import type { ReactNode } from 'react'
import { useMemo, useState } from 'react'
import { errorMessage } from '../api/client.ts'
import { useMe, useTasks } from '../api/hooks.ts'
import type { DueGroup } from '../lib/dates.ts'
import { DUE_GROUP_LABELS, DUE_GROUP_ORDER, dueGroup } from '../lib/dates.ts'
import { useNow } from '../lib/hooks.ts'
import { pl, TASK_FORMS } from '../lib/plural.ts'
import { readStore, writeStore } from '../lib/storage.ts'
import { Button } from '../ui/Button.tsx'
import type { FilterOption } from '../ui/Chip.tsx'
import { FilterChips } from '../ui/Chip.tsx'
import { EmptyState, ErrorState } from '../ui/EmptyState.tsx'
import { Section } from '../ui/List.tsx'
import { Screen, ScreenHeader } from '../ui/ScreenHeader.tsx'
import { SkeletonList } from '../ui/Skeleton.tsx'
import { useTaskActions } from '../ui/TaskActions.tsx'
import { TaskCell } from '../ui/TaskCell.tsx'

type Filter = 'all' | 'new' | 'work' | 'issue' | 'review'

const PRIORITY_RANK = { urgent: 0, high: 1, normal: 2, low: 3 } as const

function byDue(a: TaskListItemDto, b: TaskListItemDto): number {
  const ad = a.dueAt ? new Date(a.dueAt).getTime() : Number.POSITIVE_INFINITY
  const bd = b.dueAt ? new Date(b.dueAt).getTime() : Number.POSITIVE_INFINITY
  return ad - bd || PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] || b.id - a.id
}

function matches(t: TaskListItemDto, f: Filter): boolean {
  switch (f) {
    case 'all':
      return true
    case 'new':
      return t.status === 'new'
    case 'work':
      return t.status === 'accepted' || t.status === 'in_progress'
    case 'issue':
      return t.status === 'blocked' || t.pendingRequest === 'question'
    case 'review':
      return t.status === 'submitted'
  }
}

function todayLabel(now: Date): string {
  const s = new Intl.DateTimeFormat('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' }).format(now)
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export function MyTasksScreen() {
  const me = useMe()
  const meId = me.data?.id ?? 0
  const tasks = useTasks({ scope: 'mine' }, Boolean(me.data))
  const [filter, setFilterState] = useState<Filter>(() => readStore<Filter>('filter.mine', 'all'))
  const setFilter = (f: Filter): void => {
    setFilterState(f)
    writeStore('filter.mine', f)
  }
  const actions = useTaskActions(meId)
  const now = useNow()

  const all = useMemo(() => [...(tasks.data ?? [])].sort(byDue), [tasks.data])
  const toAccept = all.filter((t) => t.status === 'new')
  const visible =
    filter === 'all' ? all.filter((t) => t.status !== 'new') : all.filter((t) => matches(t, filter))

  const groups = useMemo(() => {
    const map = new Map<DueGroup, TaskListItemDto[]>()
    for (const t of visible) {
      const g = dueGroup(t.dueAt, t.isOverdue && t.status !== 'submitted', now)
      map.set(g, [...(map.get(g) ?? []), t])
    }
    return map
  }, [visible, now])

  const options: FilterOption<Filter>[] = [
    { value: 'all', label: 'Все' },
    { value: 'new', label: 'Новые', count: all.filter((t) => matches(t, 'new')).length },
    { value: 'work', label: 'В работе', count: all.filter((t) => matches(t, 'work')).length },
    { value: 'issue', label: 'Вопрос/блокер', count: all.filter((t) => matches(t, 'issue')).length },
    { value: 'review', label: 'На проверке', count: all.filter((t) => matches(t, 'review')).length },
  ]

  const overdueCount = groups.get('overdue')?.length ?? 0
  const todayCount = groups.get('today')?.length ?? 0
  const subtitle = me.data
    ? `${todayLabel(now)} · ${todayCount ? `на сегодня ${pl(todayCount, TASK_FORMS)}` : 'на сегодня задач нет'}`
    : todayLabel(now)

  const cell = (t: TaskListItemDto) => (
    <TaskCell key={t.id} task={t} meId={meId} now={now} onGlyph={(task) => actions.openMenu(task)} />
  )

  let body: ReactNode
  if (tasks.isPending || me.isPending) {
    body = <SkeletonList rows={5} />
  } else if (tasks.isError || me.isError) {
    body = <ErrorState text={errorMessage(tasks.error ?? me.error)} onRetry={() => void tasks.refetch()} />
  } else if (all.length === 0) {
    body = (
      <EmptyState
        title="Задач нет — можно выдохнуть"
        text="Новые задачи появятся здесь. Чтобы поставить задачу себе или коллеге, нажмите «+»."
      />
    )
  } else {
    body = (
      <>
        {filter === 'all' && toAccept.length > 0 && (
          <Section title="Нужно принять" count={toAccept.length} id="to-accept">
            {toAccept.map((t) => (
              <TaskCell
                key={t.id}
                task={t}
                meId={meId}
                now={now}
                onGlyph={(task) => actions.openMenu(task)}
                action={
                  <Button
                    variant="primary"
                    size="sm"
                    loading={actions.pendingTaskId === t.id}
                    onClick={() => actions.run(t, 'accept')}
                    aria-label={`Принять задачу «${t.title}»`}
                  >
                    Принять
                  </Button>
                }
              />
            ))}
          </Section>
        )}

        {visible.length === 0 && filter !== 'all' && (
          <EmptyState title="Здесь пусто" text="В этом фильтре задач нет." />
        )}

        {DUE_GROUP_ORDER.map((g) => {
          const list = groups.get(g)
          if (g === 'today' && filter === 'all' && !list?.length && visible.length > 0) {
            return (
              <Section key={g} title={DUE_GROUP_LABELS.today} id="g-today">
                <div className="cell">
                  <span className="cell__main">
                    <span className="cell__subtitle">Сегодня задач нет</span>
                  </span>
                </div>
              </Section>
            )
          }
          if (!list?.length) return null
          return (
            <Section
              key={g}
              id={`g-${g}`}
              title={DUE_GROUP_LABELS[g]}
              count={list.length}
              tone={g === 'overdue' ? 'attn' : 'default'}
              footer={
                g === 'overdue' ? 'Обновите срок или статус — постановщику так будет понятнее.' : undefined
              }
            >
              {list.map(cell)}
            </Section>
          )
        })}
      </>
    )
  }

  return (
    <Screen root label="Мои задачи">
      <ScreenHeader title="Мои задачи" />
      <p className="screen-subtitle">
        {subtitle}
        {overdueCount > 0 && <span className="text-attn"> · срок прошёл у {overdueCount}</span>}
      </p>
      {all.length > 0 && (
        <FilterChips label="Фильтр задач" options={options} value={filter} onChange={setFilter} />
      )}
      {body}
      {actions.element}
    </Screen>
  )
}
